import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header/Header';
import SEO from '../components/SEO/SEO';
import { useLanguage } from '../contexts/LanguageContext';
import { useInsights, useInsightCategories } from '../hooks/useInsights';
import { getSlugFromSymbol, CATEGORY_MAP } from '../config/categoryMapping';
import { trackFilterChange, trackSearch, trackFeatureUsage } from '../utils/analytics';
import { rewriteR2Url } from '../utils/r2Url';
import './InsightsPage.css';

// Category color mapping
const CATEGORY_COLORS = {
  BTC: { bg: 'rgba(247, 147, 26, 0.15)', color: '#f7931a', border: 'rgba(247, 147, 26, 0.3)' },
  ETH: { bg: 'rgba(98, 126, 234, 0.15)', color: '#627eea', border: 'rgba(98, 126, 234, 0.3)' },
  SOL: { bg: 'rgba(153, 69, 255, 0.15)', color: '#9945ff', border: 'rgba(153, 69, 255, 0.3)' },
  XRP: { bg: 'rgba(35, 41, 47, 0.3)', color: '#fff', border: 'rgba(255, 255, 255, 0.2)' },
  DOGE: { bg: 'rgba(186, 155, 53, 0.15)', color: '#ba9b35', border: 'rgba(186, 155, 53, 0.3)' },
  ADA: { bg: 'rgba(0, 51, 173, 0.15)', color: '#0033ad', border: 'rgba(0, 51, 173, 0.3)' },
  DEFAULT: { bg: 'rgba(167, 139, 250, 0.15)', color: '#a78bfa', border: 'rgba(167, 139, 250, 0.3)' }
};

function getCategoryStyle(category) {
  return CATEGORY_COLORS[category] || CATEGORY_COLORS.DEFAULT;
}

// Locale mapping for date formatting
const DATE_LOCALES = { en: 'en-US', ru: 'ru-RU', ko: 'ko-KR', zh: 'zh-CN' };

// Format date for display with locale support
function formatDate(dateString, lang = 'en') {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString(DATE_LOCALES[lang] || 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

// Article Card Component
function ArticleCard({ post }) {
  const { localizedPath, language } = useLanguage();
  // Get tags (fallback to category for backward compatibility)
  const tags = post.tags || [post.category];
  const primaryTag = tags[0] || post.category;
  const categoryStyle = getCategoryStyle(primaryTag);

  const handleArticleClick = () => {
    trackFeatureUsage('blog_article_click', {
      slug: post.slug,
      title: post.title,
      category: primaryTag,
      page: '/insights'
    });
  };

  return (
    <Link to={localizedPath(`/insights/${post.slug}`)} className="insight-card" onClick={handleArticleClick}>
      {(post.coverImageVariants?.thumbnail || post.coverImage) && (
        <div className="insight-card-image">
          <img src={rewriteR2Url(post.coverImageVariants?.thumbnail || post.coverImage)} alt={post.title} loading="lazy" />
        </div>
      )}
      <div className="insight-card-content">
        <div className="insight-tags-row">
          {tags.slice(0, 3).map((tag) => {
            const style = getCategoryStyle(tag);
            const isCoin = !!CATEGORY_MAP[tag];
            const badgeStyle = {
              backgroundColor: style.bg,
              color: style.color,
              borderColor: style.border
            };
            return isCoin ? (
              <Link
                key={tag}
                to={localizedPath(`/price/${tag}`)}
                className="insight-category-badge insight-tag-link"
                style={badgeStyle}
                onClick={(e) => e.stopPropagation()}
              >
                {tag}
              </Link>
            ) : (
              <span
                key={tag}
                className="insight-category-badge"
                style={badgeStyle}
              >
                {tag}
              </span>
            );
          })}
          {tags.length > 3 && (
            <span className="insight-category-badge" style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#888' }}>
              +{tags.length - 3}
            </span>
          )}
        </div>
        <h3 className="insight-card-title">{post.title}</h3>
        {post.excerpt && (
          <p className="insight-card-excerpt">{post.excerpt}</p>
        )}
        <div className="insight-card-meta">
          <span className="insight-card-author">{post.authorName || 'TraderMap Team'}</span>
          <span className="insight-card-date">{formatDate(post.publishedAt, language)}</span>
        </div>
      </div>
    </Link>
  );
}

// Category Section Component (horizontal grid)
function CategorySection({ title, category, posts, icon }) {
  const { localizedPath } = useLanguage();
  const categoryStyle = getCategoryStyle(category);

  if (!posts || posts.length === 0) return null;

  return (
    <div className="insight-category-section">
      <div className="insight-category-header">
        <h2 style={{ color: categoryStyle.color }}>
          {icon && <span className="category-icon">{icon}</span>}
          {title}
        </h2>
        <Link to={localizedPath(`/insights/${getSlugFromSymbol(category) || category.toLowerCase()}`)} className="view-all-link">
          View All
        </Link>
      </div>
      <div className="insight-category-grid">
        {posts.slice(0, 3).map(post => (
          <ArticleCard key={post._id || post.slug} post={post} />
        ))}
      </div>
    </div>
  );
}

function InsightsPage({ initialCategory, categorySlug }) {
  const { t } = useTranslation();
  const { localizedPath, language } = useLanguage();

  // State for filters
  const [selectedCategory, setSelectedCategory] = useState(initialCategory || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Fetch all posts with filters
  const { posts, total, loading: postsLoading, refetch } = useInsights({
    limit: 20,
    category: selectedCategory || null,
    search: searchQuery || null,
    lang: language
  });

  // Fetch categories for filter
  const { categories } = useInsightCategories();

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      trackSearch(searchInput.trim(), { page: '/insights', type: 'blog' });
    }
    setSearchQuery(searchInput);
  };

  const navigate = useNavigate();

  // Handle category filter
  const handleCategoryChange = (category) => {
    trackFilterChange('blog_category', category || 'all', { page: '/insights' });
    setSelectedCategory(category);
    // Navigate to SEO-friendly URL
    if (category) {
      const slug = getSlugFromSymbol(category);
      if (slug) {
        navigate(localizedPath(`/insights/${slug}`));
      }
    } else {
      navigate(localizedPath('/insights'));
    }
  };

  return (
    <>
      <SEO
        page={categorySlug ? 'insightCategory' : 'insights'}
        dynamicParams={categorySlug ? { slug: categorySlug, symbol: initialCategory } : {}}
        title={categorySlug
          ? `${initialCategory} Analysis & Insights | Tradermap`
          : 'Crypto Insights & Analysis | Tradermap'
        }
        description={categorySlug
          ? `Expert ${initialCategory} market analysis, whale tracking insights, and in-depth ${categorySlug} trading research. Stay ahead with daily updates from our analysts.`
          : 'Expert cryptocurrency market analysis, trading insights, and in-depth research on Bitcoin, Ethereum, and altcoins. Stay ahead with daily updates from our analysts.'
        }
        keywords={categorySlug
          ? `${initialCategory} analysis, ${categorySlug} analysis, ${initialCategory} trading, crypto insights, ${initialCategory} whale tracking`
          : 'crypto analysis, bitcoin analysis, ethereum analysis, crypto insights, trading analysis, market research'
        }
      />

      <Header />

      <main className="insights-page">
        <div className="insights-content">
          {/* Top Banner */}
          <div className="insights-top-banner">
            <p>Expert crypto analyses from our top analysts are with you every day</p>
          </div>


          {/* All Insights Section */}
          <section className="insights-all">
            <div className="insights-all-header">
              <h2>All Insights</h2>

              {/* Filters */}
              <div className="insights-filters">
                {/* Search */}
                <form onSubmit={handleSearch} className="insights-search">
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  <button type="submit">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"/>
                      <path d="M21 21l-4.35-4.35"/>
                    </svg>
                  </button>
                </form>

                {/* Category Filter */}
                <div className="insights-category-filter">
                  <button
                    className={`filter-btn ${selectedCategory === '' ? 'active' : ''}`}
                    onClick={() => handleCategoryChange('')}
                  >
                    All
                  </button>
                  {categories.slice(0, 6).map(cat => (
                    <button
                      key={cat.category}
                      className={`filter-btn ${selectedCategory === cat.category ? 'active' : ''}`}
                      onClick={() => handleCategoryChange(cat.category)}
                      style={selectedCategory === cat.category ? {
                        backgroundColor: getCategoryStyle(cat.category).bg,
                        borderColor: getCategoryStyle(cat.category).border
                      } : {}}
                    >
                      {cat.category} ({cat.count})
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Posts Grid */}
            {postsLoading ? (
              <div className="insights-loading">
                <div className="loading-spinner"></div>
                <p>Loading insights...</p>
              </div>
            ) : posts.length > 0 ? (
              <div className="insights-grid">
                {posts.map(post => (
                  <ArticleCard key={post._id || post.slug} post={post} />
                ))}
              </div>
            ) : (
              <div className="insights-empty">
                <p>No articles found</p>
                {(selectedCategory || searchQuery) && (
                  <button
                    className="clear-filters-btn"
                    onClick={() => {
                      setSelectedCategory('');
                      setSearchQuery('');
                      setSearchInput('');
                    }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            )}

          </section>
        </div>
      </main>
    </>
  );
}

export default InsightsPage;
